import { Image, Text, View, Font, Page } from '@react-pdf/renderer'
import Html from 'react-pdf-html'
import { useTranslation } from 'react-i18next'
import RobotoLight from '../../fonts/Roboto/Roboto-Light.ttf'
import RobotoBold from '../../fonts/Roboto/Roboto-Bold.ttf'
import RobotoRegular from '../../fonts/Roboto/Roboto-Regular.ttf'
import '../../i18n/i18n'

Font.register({
  family: 'Roboto',
  fonts: [
    { src: RobotoLight, fontWeight: 300 }, // Light
    { src: RobotoRegular, fontWeight: 400 }, // ✅ Regular/Normal
    { src: RobotoBold, fontWeight: 600 } // Bold
  ]
})

export const style = {
  page: {
    fontSize: 11,
    paddingTop: 50, // Marge normale
    marginBottom: 80, // Espace réservé pour le pied de page
    paddingHorizontal: 50,
    lineHeight: 1.5
  },

  spaceBetween: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    color: '#3E3E3E'
  },

  titleContainer: { flexDirection: 'row' },
  rightContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 24
  },
  rightContainerNoMT: {
    flexDirection: 'row',
    justifyContent: 'flex-end'
  },
  footer: {
    position: 'absolute',
    paddingTop: -5,
    top: 60,
    left: 0,
    right: 0,
    textAlign: 'center',
    fontSize: 10,
    color: '#666',
    fontFamily: 'Roboto',
    zIndex: 100
  },
  footerContainer: { flexDirection: 'column' },
  logo: { width: 120 },

  reportTitle: {
    fontFamily: 'Roboto',
    fontWeight: 300,
    fontSize: 36,
    textAlign: 'center'
  },
  reportTitleSmall: {
    fontFamily: 'Roboto',
    fontWeight: 300,
    fontSize: 28,
    textAlign: 'center'
  },

  addressBlock: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 20
  },

  bold: { fontFamily: 'Roboto', fontSize: 14, fontWeight: 600 },
  boldWithSpace: {
    fontFamily: 'Roboto',
    fontSize: 14,
    fontWeight: 600,
    marginRight: 5
  },

  address: { fontWeight: 400, fontSize: 14 },

  theader: {
    flexBasis: 100,
    fontFamily: 'Roboto',
    marginTop: 20,
    color: 'black',
    fontSize: 14,
    fontWeight: 600,
    padding: 10.5,
    alignContent: 'left',
    borderColor: '#eceeef',
    borderTopWidth: 1,
    borderBottomWidth: 1
  },
  theaderAccounting: {
    flexBasis: 100,
    fontFamily: 'Roboto',
    marginTop: 20,
    color: 'black',
    fontSize: 12,
    fontWeight: 600,
    padding: 10.5,
    alignContent: 'left',
    borderColor: '#eceeef',
    borderTopWidth: 1,
    borderBottomWidth: 1
  },
  theader2: {
    flexBasis: 220,
    fontFamily: 'Roboto',
    marginTop: 20,
    color: 'black',
    fontSize: 14,
    fontWeight: 600,
    padding: 10.5,
    alignContent: 'center',
    borderColor: '#eceeef',
    borderTopWidth: 1,
    borderBottomWidth: 1
  },
  theaderAccounting2: {
    flexBasis: 220,
    fontFamily: 'Roboto',
    marginTop: 20,
    color: 'black',
    fontSize: 12,
    fontWeight: 600,
    padding: 10.5,
    alignContent: 'left',
    borderColor: '#eceeef',
    borderTopWidth: 1,
    borderBottomWidth: 1
  },
  tbody: { textAlign: 'right', flexBasis: 100, fontSize: 14, padding: 10.5 },
  tbodyAccounting: { textAlign: 'left', flexBasis: 100, fontSize: 10, padding: 10.5 },
  tbodyfill: {
    textAlign: 'right',
    flexBasis: 100,
    fontSize: 14,
    padding: 10.5,
    backgroundColor: '#0000000d'
  },

  tbody2: { flexBasis: 220, fontSize: 14, padding: 10.5 },
  tbodyAccounting2: { flexBasis: 220, textAlign: 'left', fontSize: 10, padding: 10.5 },
  tbodyfill2: {
    flexBasis: 220,
    fontSize: 14,
    padding: 10.5,
    backgroundColor: '#0000000d'
  },

  tbodytotal: {
    textAlign: 'right',
    fontSize: 14,
    padding: 10.5,
    flex: 1,
    borderColor: 'whitesmoke',
    borderTopWidth: 1
  },
  total: { fontSize: 14, textAlign: 'left', padding: 10.5, flex: 1.5 },
  totalAccounting: { fontSize: 10, padding: 10.5, flex: 1.5 },
  conditions: { color: '#666', fontSize: 9 },
  conditions_tva: { color: '#666', fontSize: 9, textAlign: 'right' },
  footerText: { fontSize: 9, textAlign: 'right', color: '#666' }
}

export const Header = ({ styles, img, title, titleSize = 'normal' }) => {
  if (!styles) return null

  const firstChar = img?.charAt(0)
  const extension = {
    '/': 'jpeg',
    i: 'png',
    R: 'gif',
    U: 'webp'
  }[firstChar]

  return (
    <View style={styles.titleContainer}>
      <View style={styles.spaceBetween}>
        {img ? (
          <Image style={styles.logo} src={`data:image/${extension};base64,${img}`} />
        ) : (
          <View style={styles.logo} />
        )}
        <View>
          <Text style={titleSize === 'normal' ? styles.reportTitle : styles.reportTitleSmall}>{title}</Text>
        </View>
      </View>
    </View>
  )
}

export const DocumentDate = ({ date, styles }) => {
  const { t } = useTranslation()
  if (!styles) return null

  const dateConv = t('dates.localisedDate', { date: date ? new Date(date) : new Date() })

  return (
    <View style={styles.rightContainer}>
      <Text style={styles.bold}>{dateConv}</Text>
    </View>
  )
}

export const Subject = ({ styles, document }) => {
  if (!styles || !document?.summary) return null

  return (
    <>
      <Text style={[styles.bold, { marginTop: 20 }]}>{document.summary}</Text>
      {document.description && <Text style={styles.address}>{document.description}</Text>}
    </>
  )
}

export const CustomerAddress = ({ styles, company, customer }) => {
  if (!styles || !company || !customer) return null

  const renderAddress = (entity) => (
    <>
      <Text style={styles.bold}>{entity.name || entity.customer_name}</Text>
      <Text style={styles.address}>{entity.address1}</Text>
      {entity.address2 && <Text style={styles.address}>{entity.address2}</Text>}
      <Text style={styles.address}>
        {entity.zip} {entity.city}
      </Text>
    </>
  )

  return (
    <View style={styles.addressBlock}>
      <View>{renderAddress(company)}</View>
      <View>{renderAddress(customer)}</View>
    </View>
  )
}

export const TableHead = ({ styles, headers }) => {
  if (!styles || !headers) return null

  return (
    <View style={{ width: '100%', flexDirection: 'row', marginTop: 10 }}>
      {headers.map((header) => (
        <View key={header.name} style={styles[header.headerStyle]}>
          <Text>{header.label}</Text>
        </View>
      ))}
    </View>
  )
}

export const TableBody = ({ headers, lines, styles }) => {
  if (!styles || !headers || !lines) return null

  return lines.map((line, index) => (
    <View key={line.id} wrap={false}>
      <View style={{ width: '100%', flexDirection: 'row' }}>
        {headers.map((header) => (
          <View key={header.name} style={index % 2 === 0 ? styles[header.bodyStyle] : styles[header.bodyStyle2]}>
            <Text>{line[header.name]}</Text>
          </View>
        ))}
      </View>
    </View>
  ))
}

export const TableTotal = ({ styles, total, company }) => {
  if (!styles || !total) return null

  return (
    <>
      <View style={{ width: '100%', flexDirection: 'row' }}>
        <View style={styles.total} />
        <View style={styles.total} />
        <View style={[styles.tbodytotal, styles.bold]}>
          <Text>Total HT</Text>
        </View>
        <View style={styles.tbodytotal}>
          <Text>{total}€</Text>
        </View>
      </View>
      {company?.type === 'micro' && <Text style={styles.conditions_tva}>TVA non applicable, art. 293 B du CGI</Text>}
    </>
  )
}

export const Payment = ({ styles, customer, settings }) => {
  if (!styles || !customer || !settings) return null

  const paymentConditions = customer.customer_type === 2 ? settings.payment_conditions : settings.payment_conditions_pro
  const paymentMethods =
    customer.customer_type === 2
      ? settings.payment_methods.replaceAll(',', ', ')
      : settings.payment_methods_pro.replaceAll(',', ', ')

  return (
    <View style={{ marginTop: 20 }}>
      <Text style={styles.conditions}>Conditions de paiement : {paymentConditions}</Text>
      <Text style={styles.conditions}>Règlement par : {paymentMethods}</Text>
    </View>
  )
}

export const Footer = ({ styles, company }) => {
  if (!styles || !company) return null

  return (
    <View
      fixed
      style={{
        position: 'absolute',
        bottom: 0, // Positionné au bas absolu
        left: 0,
        right: 0,
        height: 80, // Hauteur totale de la zone réservée par marginBottom: 70
        backgroundColor: 'white', // Superposition forcée
        paddingHorizontal: 50, // Appliquer le même padding horizontal que la page
        justifyContent: 'space-between',
        flexDirection: 'column'
      }}
    >
      <View style={{ paddingTop: 5 }}>
        <View style={styles.footerContainer}>
          <Text
            style={styles.footerText}
          >{`${company.name} - N° SIRET ${company.siret} - Code APE ${company.ape}`}</Text>
          <Text
            style={styles.footerText}
          >{`${company.address1} - ${company.zip} ${company.city} - ${company.phone}`}</Text>
          <Text style={styles.footerText}>{`${company.email} - ${company.website}`}</Text>
        </View>
      </View>
      <Text style={styles.footer} render={({ pageNumber, totalPages }) => `Page ${pageNumber} sur ${totalPages}`} />
    </View>
  )
}

export const CGV = ({ styles, customer, company }) => {
  if (!styles) return null
  const cgv = customer?.customer_type === 2 ? company?.cgv_particulier : company?.cgv_pro
  const showCGV = cgv && cgv !== '<p><br></p>'
  if (!showCGV) return null

  return (
    <Page size='A4' style={styles.page}>
      <Html>{cgv}</Html>
      <View
        fixed
        style={{
          position: 'absolute',
          bottom: 0, // Positionné au bas absolu
          left: 0,
          right: 0,
          height: 80, // Hauteur totale de la zone réservée par marginBottom: 70
          backgroundColor: 'white', // Superposition forcée
          paddingHorizontal: 50, // Appliquer le même padding horizontal que la page
          justifyContent: 'space-between',
          flexDirection: 'column'
        }}
      >
        <View>
          <Text
            fixed
            style={styles.footer}
            render={({ pageNumber, totalPages }) => `Page ${pageNumber} sur ${totalPages}`}
          />
        </View>
      </View>
    </Page>
  )
}
